import * as React from 'react';
import { Button, Checkbox, Dropdown, Input, MessageBar, MessageBarBody, Option, Radio, RadioGroup, Spinner, Textarea } from '@fluentui/react-components';
import { AddRegular, ArrowUploadRegular, SearchRegular } from '@fluentui/react-icons';
import { errorText, useApp } from '../app/context';
import { useHashParts } from '../app/router';
import type { IFields } from '../lists/ListStore';
import { findConflicts, type IConflictHit, nameWords, splitNames } from '../records/conflict';
import { clientsFromCsv, type IImportedClient } from '../records/csv';
import { applicationView, type IApplicationView, type IClientView, localToday, personView, stageLabel } from '../records/model';
import { useLoad } from '../records/useLoad';
import { getProgram } from '../../api/src/programs';

const ID_METHODS: string[] = ['In person, passport', 'In person, government photo ID', 'Virtually (video), government photo ID', 'Credit file', 'Two documents from different sources'];

/** Who verified the client's identity: someone on the firm's staff list (Settings), or typed in when the list is empty. */
function VerifiedBy(props: { value: string; onChange: (v: string) => void }): React.ReactElement {
  const app = useApp();
  const staff: string[] = (app.workspace.firm?.staff ?? []).map(x => x.name);
  const me: string = app.account.username.toLowerCase();
  const mine: string | undefined = (app.workspace.firm?.staff ?? []).filter(x => (x.email ?? '').toLowerCase() === me)[0]?.name;
  React.useEffect(() => {
    if (!props.value && mine) {
      props.onChange(mine);
    }
  }, [mine]);
  return (
    <label>By
      {staff.length
        ? (
          <Dropdown value={props.value} selectedOptions={props.value ? [props.value] : []} onOptionSelect={(_, d) => props.onChange(d.optionValue ?? '')} placeholder="Choose who verified it">
            {staff.map(n => <Option key={n} value={n}>{n}</Option>)}
          </Dropdown>
        )
        : <Input value={props.value} onChange={(_, d) => props.onChange(d.value)} placeholder="Who verified it" />}
    </label>
  );
}

/** Clients: the list, a new client (with its conflict check), or one client's page. */
export default function Clients(): React.ReactElement {
  const { parts } = useHashParts();
  const app = useApp();
  if (!app.workspace.firm?.listsReady) {
    return <NotSetUp />;
  }
  if (parts[0] === 'new') {
    return <NewClient />;
  }
  if (parts[0]) {
    return <ClientPage clientKey={parts[0]} />;
  }
  return <ClientList />;
}

export function NotSetUp(): React.ReactElement {
  return (
    <main className="page">
      <div className="card" style={{ gap: 10 }}>
        <strong>Your firm's lists aren't set up yet.</strong>
        <span className="small" style={{ fontSize: 15 }}>Finish the firm setup to start adding clients and applications.</span>
        <Button appearance="primary" as="a" href="#/setup" style={{ alignSelf: 'flex-start' }}>Go to firm setup</Button>
      </div>
    </main>
  );
}

function ClientList(): React.ReactElement {
  const app = useApp();
  const { data, error, reload } = useLoad(() => app.records.allClients(), [app.records]);
  const fileInput = React.useRef<HTMLInputElement>(null);
  const [importing, setImporting] = React.useState<{ clients: IImportedClient[]; problems: string[]; skipped: number } | undefined>(undefined);
  const [importState, setImportState] = React.useState<string | undefined>(undefined);

  const readFile = async (file: File): Promise<void> => {
    const r = clientsFromCsv(await file.text());
    // Clients already in the records (same name and number) aren't added twice.
    const have: { [k: string]: boolean } = {};
    for (const c of data ?? []) {
      have[`${nameWords(c.name).join(' ')}|${c.number ?? ''}`] = true;
    }
    const fresh: IImportedClient[] = r.clients.filter(c => !have[`${nameWords(c.name).join(' ')}|${c.number ?? ''}`]);
    setImporting({ clients: fresh, problems: r.problems, skipped: r.clients.length - fresh.length });
  };

  const runImport = async (): Promise<void> => {
    if (!importing) {
      return;
    }
    const batch = app.records.writer.batch();
    let n: number = 0;
    try {
      for (const c of importing.clients) {
        setImportState(`Adding ${++n} of ${importing.clients.length}…`);
        await batch.create(app.records.clients, {
          Title: c.name, OtherNames: c.otherNames, BirthDate: c.birthDate, Email: c.email, Phone: c.phone, ClientNumber: c.number
        }, c.name, `Imported the client ${c.name}`);
      }
      setImporting(undefined);
      setImportState(undefined);
      app.toast({ text: `Added ${n} client${n === 1 ? '' : 's'}. Record a conflict check and ID verification for each before opening an application.` });
      reload();
    } catch (e) {
      setImportState(`Stopped after ${n - 1}: ${errorText(e)}`);
    }
  };
  const { query } = useHashParts();
  const [q, setQ] = React.useState<string>(() => query.get('q') ?? '');
  React.useEffect(() => { setQ(query.get('q') ?? ''); }, [query.get('q')]); // eslint-disable-line react-hooks/exhaustive-deps
  const words: string[] = nameWords(q);
  const shown: IClientView[] = (data ?? [])
    .filter(c => !words.length || words.every(w => nameWords(`${c.name} ${c.otherNames ?? ''} ${c.number ?? ''}`).some(x => x.indexOf(w) === 0)))
    .sort((a, b) => a.name.localeCompare(b.name));
  const waiting: number = (data ?? []).filter(c => !c.conflictOn || !c.idOn).length;
  return (
    <main className="page">
      <div className="page-head">
        <div>
          <h1>Clients</h1>
          {data && <p>{data.length} client{data.length === 1 ? '' : 's'}{waiting ? <> · <span className="needs-you">{waiting} can't be taken on yet</span></> : null}</p>}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button icon={<ArrowUploadRegular />} onClick={() => fileInput.current?.click()} disabled={!data}>Import from CSV</Button>
          <Button appearance="primary" icon={<AddRegular />} as="a" href="#/clients/new">New client</Button>
        </div>
        <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) { void readFile(f); } }} />
      </div>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {importing && (
        <section className="card" style={{ gap: 10 }}>
          <h2>Import clients</h2>
          <span style={{ fontSize: 15 }}>{importing.clients.length} client{importing.clients.length === 1 ? '' : 's'} to add{importing.skipped ? `; ${importing.skipped} already in your records, left out` : ''}.</span>
          {importing.problems.length > 0 && <ul className="steps-list small">{importing.problems.slice(0, 20).map(p => <li key={p}>{p}</li>)}</ul>}
          <span className="small" style={{ fontSize: 14 }}>Imported clients have no conflict check or ID verification recorded yet: record both on each client's page before opening an application. Columns read: Name, Other names, Date of birth (YYYY-MM-DD), Email, Phone, Client number.</span>
          {importState && <span className="small">{importState}</span>}
          <div style={{ display: 'flex', gap: 10 }}>
            <Button appearance="primary" disabled={!importing.clients.length || (!!importState && importState.indexOf('Adding') === 0)} onClick={() => { void runImport(); }}>Add them</Button>
            <Button onClick={() => { setImporting(undefined); setImportState(undefined); }}>Cancel</Button>
          </div>
        </section>
      )}
      <Input aria-label="Search clients" contentBefore={<SearchRegular />} placeholder="Search by name, other names or client number" value={q} onChange={(_, d) => setQ(d.value)} style={{ maxWidth: 480 }} />
      {!data && !error && <Spinner size="small" label="Loading clients…" labelPosition="after" />}
      {data && shown.length === 0 && <div className="card"><span className="small" style={{ fontSize: 15 }}>{data.length ? 'No client matches that.' : 'No clients yet. Add your first one.'}</span></div>}
      {shown.length > 0 && (
        <table className="data-table">
          <thead><tr><th>Client</th><th>Number</th><th>Before taking on</th></tr></thead>
          <tbody>
            {shown.map(c => (
              <tr key={c.key}>
                <td><a href={`#/clients/${encodeURIComponent(c.key)}`}>{c.name}</a>{c.restricted && <span className="muted"> · restricted</span>}{c.otherNames && <div className="small">Also: {c.otherNames}</div>}</td>
                <td className="mono muted">{c.number ?? ''}</td>
                <td>{c.conflictOn && c.idOn
                  ? <span className="muted">Done</span>
                  : <span className="needs-you" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span className="flag" aria-hidden="true" />{!c.conflictOn && !c.idOn ? 'Conflict check and ID' : !c.idOn ? 'Verify ID' : 'Conflict check'}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}

/** Everyone in the firm's records, for the conflict check: clients (and their other names) and people on applications. */
async function conflictCandidates(app: ReturnType<typeof useApp>): Promise<Parameters<typeof findConflicts>[1]> {
  const [clients, people, applications] = await Promise.all([app.records.allClients(), app.records.fromSets(set => set.people.all()), app.records.fromSets(set => set.applications.all())]);
  const appTitle: { [key: string]: string } = {};
  for (const a of applications) {
    appTitle[a.key] = String(a.fields.Title ?? '');
  }
  return [
    ...clients.map(c => ({ candidate: { where: 'Client', name: c.name, detail: c.number, key: c.key }, names: [c.name, ...splitNames(c.otherNames)] })),
    ...people.map(personView).map(p => ({
      candidate: { where: 'Person on an application', name: p.name, detail: appTitle[String(p.rec.fields.ApplicationKey ?? '')] }, names: [p.name]
    }))
  ];
}

/** The conflict check, for new or existing clients: search, review each match, record the outcome. */
function ConflictCheck(props: { names: string[]; skipClientKey?: string; onResult: (summary: string | undefined) => void }): React.ReactElement {
  const app = useApp();
  const [hits, setHits] = React.useState<IConflictHit[] | undefined>(undefined);
  const [checkedFor, setCheckedFor] = React.useState<string>('');
  const [outcome, setOutcome] = React.useState<string>('');
  const [why, setWhy] = React.useState<string>('');
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const namesKey: string = props.names.join('|');
  const checked: boolean = !!hits && checkedFor === namesKey;

  const check = async (): Promise<void> => {
    setError(undefined);
    setWorking(true);
    try {
      const candidates = (await conflictCandidates(app)).filter(c => !props.skipClientKey || c.candidate.key !== props.skipClientKey);
      setHits(findConflicts(props.names, candidates));
      setCheckedFor(namesKey);
      setOutcome('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const summary: string | undefined = !checked ? undefined : hits!.length === 0
    ? 'No possible conflict found.'
    : outcome === 'clear' && why.trim()
      ? `Possible matches reviewed, no conflict: ${why.trim()} Matches checked: ${hits!.map(h => `${h.name} (${h.where}${h.detail ? `, ${h.detail}` : ''})`).join('; ')}.`
      : undefined;
  React.useEffect(() => props.onResult(summary), [summary]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="card" style={{ gap: 12 }}>
      <h2>Conflict check</h2>
      <span className="small" style={{ fontSize: 14 }}>Looks through every client and every person on an application in your records, by all of this client's names.</span>
      {error && <span className="small" style={{ color: 'var(--red)' }}>{error}</span>}
      <Button disabled={working || !props.names.length} onClick={() => { void check(); }} style={{ alignSelf: 'flex-start' }}>{working ? 'Checking…' : checked ? 'Check again' : 'Check for conflicts'}</Button>
      {hits && !checked && <span className="small">The names changed since the last check: check again.</span>}
      {checked && hits!.length === 0 && <MessageBar intent="success"><MessageBarBody>No possible conflict found in your records.</MessageBarBody></MessageBar>}
      {checked && hits!.length > 0 && (
        <>
          <MessageBar intent="warning"><MessageBarBody>{hits!.length} possible match{hits!.length === 1 ? '' : 'es'} in your records. Review each one.</MessageBarBody></MessageBar>
          <ul className="steps-list">
            {hits!.map((h, i) => <li key={i}><strong>{h.name}</strong> · {h.where}{h.detail ? ` · ${h.detail}` : ''} · {h.strength === 'same' ? 'same name' : 'similar name'} as "{h.matched}"</li>)}
          </ul>
          <RadioGroup value={outcome} onChange={(_, d) => setOutcome(d.value)}>
            <Radio value="clear" label="I reviewed them: there's no conflict" />
            <Radio value="conflict" label="There's a conflict: don't take the client on" />
          </RadioGroup>
          {outcome === 'clear' && <Textarea placeholder="Why there's no conflict (e.g. a different person, different date of birth)" value={why} onChange={(_, d) => setWhy(d.value)} />}
          {outcome === 'conflict' && <span className="small" style={{ color: 'var(--red)' }}>The client can't be taken on while there's a conflict.</span>}
        </>
      )}
    </section>
  );
}

function NewClient(): React.ReactElement {
  const app = useApp();
  const [f, setF] = React.useState<{ [k: string]: string }>({});
  const [conflict, setConflict] = React.useState<string | undefined>(undefined);
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [restrictedClient, setRestrictedClient] = React.useState<boolean>(false);
  const set = (k: string) => (_: unknown, d: { value: string }) => setF(x => ({ ...x, [k]: d.value }));
  const names: string[] = [f.name ?? '', ...splitNames(f.otherNames)].filter(n => n.trim());
  const canSave: boolean = !working && !!(f.name ?? '').trim() && !!conflict;

  const save = async (): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      const fields: IFields = {
        Title: f.name.trim(), OtherNames: f.otherNames, BirthDate: f.birthDate, Email: f.email, Phone: f.phone, Language: f.language,
        UCI: f.uci, ClientNumber: f.number, Notes: f.notes,
        ConflictResult: conflict, ConflictBy: app.account.username, ConflictOn: new Date().toISOString()
      };
      if (f.idMethod && f.idOn) {
        Object.assign(fields, { IdMethod: f.idMethod, IdBy: f.idBy || app.account.username, IdOn: f.idOn });
      }
      if (restrictedClient) {
        await app.records.requireRestrictedClients();
      }
      const target = restrictedClient ? app.records.restricted! : app.records.main;
      const r = await target.writer.batch().create(target.clients, fields, f.name.trim(), `Added the client ${f.name.trim()} (conflict check recorded)`);
      window.location.hash = `/clients/${encodeURIComponent(r.key)}`;
    } catch (e) {
      setError(errorText(e));
      setWorking(false);
    }
  };

  return (
    <main className="page" style={{ maxWidth: 900 }}>
      <div className="page-head"><div><h1>New client</h1><p>Run the conflict check before taking the client on.</p></div></div>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      <section className="card" style={{ gap: 12 }}>
        <div className="form-grid">
          <label>Full name (as on the passport)<Input value={f.name ?? ''} onChange={set('name')} placeholder="Last name, first name" /></label>
          <label>Other names they've used<Input value={f.otherNames ?? ''} onChange={set('otherNames')} placeholder="Separate with ;" /></label>
          <label>Date of birth<Input type="date" value={f.birthDate ?? ''} onChange={set('birthDate')} /></label>
          <label>Email<Input value={f.email ?? ''} onChange={set('email')} /></label>
          <label>Phone<Input value={f.phone ?? ''} onChange={set('phone')} /></label>
          <label>Preferred language<Input value={f.language ?? ''} onChange={set('language')} /></label>
          <label>UCI (if they have one)<Input value={f.uci ?? ''} onChange={set('uci')} /></label>
          <label>Client number<Input value={f.number ?? ''} onChange={set('number')} /></label>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: 'var(--ink-3)' }}>Notes<Textarea value={f.notes ?? ''} onChange={set('notes')} /></label>
        {app.records.restricted && (
          <Checkbox checked={restrictedClient} onChange={(_, x) => setRestrictedClient(!!x.checked)}
            label={<span><strong>Restricted client</strong> (refugee or private sponsorship): kept in the restricted clients list, open only to your "Restricted files" group.</span>} />
        )}
      </section>

      <ConflictCheck names={names} onResult={setConflict} />

      <section className="card" style={{ gap: 12 }}>
        <h2>Identity verification</h2>
        <span className="small" style={{ fontSize: 14 }}>Record how the client's identity was verified. You can also do this later, but an application can't be opened until it's recorded.</span>
        <div className="form-grid">
          <label>How
            <Dropdown value={f.idMethod ?? ''} selectedOptions={f.idMethod ? [f.idMethod] : []} onOptionSelect={(_, d) => setF(x => ({ ...x, idMethod: d.optionValue ?? '' }))} placeholder="Choose">
              {ID_METHODS.map(m => <Option key={m} value={m}>{m}</Option>)}
            </Dropdown>
          </label>
          <VerifiedBy value={f.idBy ?? ''} onChange={v => setF(x => ({ ...x, idBy: v }))} />
          <label>On<Input type="date" value={f.idOn ?? ''} max={localToday()} onChange={set('idOn')} /></label>
        </div>
      </section>

      <div style={{ display: 'flex', gap: 10 }}>
        <Button appearance="primary" disabled={!canSave} onClick={() => { void save(); }}>{working ? 'Saving…' : 'Add client'}</Button>
        <Button as="a" href="#/clients">Cancel</Button>
      </div>
    </main>
  );
}

function ClientPage(props: { clientKey: string }): React.ReactElement {
  const app = useApp();
  const { data, error, reload } = useLoad(async () => {
    const client = await app.records.findClient(props.clientKey);
    const applications = client ? (await app.records.fromSets(set => set.applications.query({ ClientKey: props.clientKey }))).map(applicationView) : [];
    return { client, applications };
  }, [app.records, props.clientKey]);
  const [conflict, setConflict] = React.useState<string | undefined>(undefined);
  const [idMethod, setIdMethod] = React.useState<string>('');
  const [idOn, setIdOn] = React.useState<string>('');
  const [idBy, setIdBy] = React.useState<string>('');
  const [working, setWorking] = React.useState<boolean>(false);
  const [problem, setProblem] = React.useState<string | undefined>(undefined);

  if (error) {
    return <main className="page"><MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar></main>;
  }
  if (!data) {
    return <main className="page"><Spinner size="small" label="Loading…" labelPosition="after" /></main>;
  }
  const c: IClientView | undefined = data.client;
  if (!c || c.rec.fields.Removed === true) {
    return <main className="page"><div className="card">This client isn't in your records. <a href="#/clients">Back to clients</a></div></main>;
  }

  const recordId = async (): Promise<void> => {
    setWorking(true);
    setProblem(undefined);
    try {
      const set = app.records.setOf(c);
      await set.writer.batch().update(set.clients, c.rec, { IdMethod: idMethod, IdBy: idBy || app.account.username, IdOn: idOn }, c.name, `Recorded how ${c.name}'s identity was verified`);
      reload();
    } catch (e) {
      setProblem(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const recordConflict = async (): Promise<void> => {
    setWorking(true);
    setProblem(undefined);
    try {
      const set = app.records.setOf(c);
      await set.writer.batch().update(set.clients, c.rec, { ConflictResult: conflict, ConflictBy: app.account.username, ConflictOn: new Date().toISOString() }, c.name, `Recorded the conflict check for ${c.name}`);
      reload();
    } catch (e) {
      setProblem(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const apps: IApplicationView[] = data.applications.filter(a => a.rec.fields.Removed !== true);
  return (
    <main className="page" style={{ maxWidth: 960 }}>
      <div className="page-head">
        <div><h1>{c.name}{c.restricted && <span className="tag-warn" style={{ marginLeft: 10, fontSize: 13, verticalAlign: 'middle' }}>Restricted</span>}</h1><p><a href="#/clients">Clients</a>{c.number ? ` · ${c.number}` : ''}</p></div>
        <Button appearance="primary" icon={<AddRegular />} as="a" href={`#/applications/new?client=${encodeURIComponent(c.key)}`}>Open an application</Button>
      </div>
      {problem && <MessageBar intent="error"><MessageBarBody>{problem}</MessageBarBody></MessageBar>}
      <section className="card">
        <div className="facts">
          <span className="muted">Other names</span><span>{c.otherNames ?? '—'}</span>
          <span className="muted">Date of birth</span><span>{c.birthDate ?? '—'}</span>
          <span className="muted">Email</span><span>{c.email ?? '—'}</span>
          <span className="muted">Phone</span><span>{c.phone ?? '—'}</span>
          <span className="muted">Language</span><span>{c.language ?? '—'}</span>
          <span className="muted">UCI</span><span>{c.uci ?? '—'}</span>
          <span className="muted">Conflict check</span><span>{c.conflictResult ?? 'Not recorded'}{c.conflictOn ? <span className="small"> · {c.conflictBy}, {new Date(c.conflictOn).toLocaleDateString()}</span> : null}</span>
          <span className="muted">Identity verified</span><span>{c.idOn ? `${c.idMethod} · ${c.idBy}, ${c.idOn}` : 'Not recorded'}</span>
          {c.notes && <><span className="muted">Notes</span><span style={{ whiteSpace: 'pre-wrap' }}>{c.notes}</span></>}
        </div>
      </section>
      {!c.conflictOn && (
        <>
          <ConflictCheck names={[c.name, ...splitNames(c.otherNames)]} skipClientKey={c.key} onResult={setConflict} />
          <Button appearance="primary" disabled={working || !conflict} onClick={() => { void recordConflict(); }} style={{ alignSelf: 'flex-start' }}>Record the conflict check</Button>
        </>
      )}
      {!c.idOn && (
        <section className="card" style={{ gap: 10 }}>
          <h2>Record identity verification</h2>
          <div className="form-grid">
            <label>How
              <Dropdown value={idMethod} selectedOptions={idMethod ? [idMethod] : []} onOptionSelect={(_, d) => setIdMethod(d.optionValue ?? '')} placeholder="Choose">
                {ID_METHODS.map(m => <Option key={m} value={m}>{m}</Option>)}
              </Dropdown>
            </label>
            <VerifiedBy value={idBy} onChange={setIdBy} />
            <label>On<Input type="date" value={idOn} max={localToday()} onChange={(_, d) => setIdOn(d.value)} /></label>
          </div>
          <Button disabled={working || !idMethod || !idOn || !idBy} onClick={() => { void recordId(); }} style={{ alignSelf: 'flex-start' }}>Save</Button>
        </section>
      )}
      <section className="home-section">
        <div className="section-head"><h2>Applications</h2></div>
        {apps.length === 0
          ? <div className="card"><span className="small" style={{ fontSize: 15 }}>No applications yet.</span></div>
          : (
            <table className="data-table">
              <thead><tr><th>Application</th><th>Stage</th><th>Opened</th><th>Items outstanding</th></tr></thead>
              <tbody>{apps.map(a => (
                <tr key={a.key}>
                  <td><a href={`#/applications/${encodeURIComponent(a.key)}`}>{getProgram(a.programId)?.short ?? a.title}</a></td>
                  <td>{stageLabel(a.stage)}</td><td>{a.opened ?? '—'}</td><td>{a.outstanding}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
      </section>
    </main>
  );
}
